import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { AppFeatures, AppSettings } from '@core/library/app-settings';
import {
  LanguageLookup,
  Optional,
  StaffBasicInfo,
} from '@core/models/global-entities/global-entities';
import { CacheManagementService } from '@core/services/cache-management.service';
import { GatewaySignalrService } from '@core/services/gateway-signalr.service';
import { NavHeaderService } from '@core/services/nav-header.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { v4 as uuidv4 } from 'uuid';
import { Subject, interval } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@UntilDestroy()
@Component({
  selector: 'kmt-page-header',
  templateUrl: './page-header.component.html',
  styleUrls: ['./page-header.component.scss'],
})
export class PageHeaderComponent implements OnInit, OnDestroy {
  @Input() title: string;
  user: StaffBasicInfo;
  guid: string;
  realtimeConnection = false;
  supportedLanguages: LanguageLookup[] = [];
  AppSettings = AppSettings;
  AppFeatures = AppFeatures;
  IdeasURL = 'https://unifocus.ideas.aha.io/';
  SupportURL = 'https://ufjira.atlassian.net/servicedesk/customer/portal/10';
  ReleaseNotes =
    'https://help.unifocus.com/help/Content/unifocus-release-notes.htm';
  OnlineHelp =
    'https://help.unifocus.com/help/Content/UniFocus-User-Documentation.htm';
  PropertyDateTime = '';
  private destroy$ = new Subject<void>();

  constructor(
    public cacheService: CacheManagementService,
    public sanitizer: DomSanitizer,
    public navHeader: NavHeaderService,
    public gatewaySignalR: GatewaySignalrService,
  ) {
    this.user = this.cacheService.LoggedInUser;
    this.guid = uuidv4();
    this.realtimeConnection = this.gatewaySignalR.isConnectionEstablished;
    this.supportedLanguages = this.getLanguages();
    // this.userImage = sanitizer.bypassSecurityTrustResourceUrl(
    //   'http://ksv5.knowcross.com:4400/Pages/Staff_Image?StaffID=33932'
    // );
  }

  ngOnInit(): void {
    this.initializeDateTime();
    this.gatewaySignalR.connectionEstablished
      .pipe(untilDestroyed(this))
      .subscribe((value) => (this.realtimeConnection = value));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeDateTime(): void {
    if (AppSettings.IsRunningInSingularity) {
      this.updatePropertyDateTime();
      interval(1000)
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => this.updatePropertyDateTime());
    }
  }

  private updatePropertyDateTime(): void {
    const activeUser = this.cacheService.LoggedInUser;
    if (!activeUser) return;

    const property = this.getProperty(activeUser.PropertyId);
    if (!property) return;

    const timeZoneOffsetHours = property.BaseUtcOffset / 3600;
    const now = new Date();
    const tzDifference = timeZoneOffsetHours * 60 + now.getTimezoneOffset();
    const propertyTime = new Date(now.getTime() + tzDifference * 60000);

    this.PropertyDateTime = `${propertyTime.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: '2-digit',
    })} ${propertyTime.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    })}`;
  }

  private getProperty(propertyId: number): any {
    const properties = this.cacheService.LoggedInUser?.Properties;
    return (
      properties?.find((property) => property.PropertyId === propertyId) || null
    );
  }

  getStaffImage(user: StaffBasicInfo): Optional<SafeResourceUrl> {
    if (!user || !user.HasImage) {
      return null;
    }
    return this.sanitizer.bypassSecurityTrustResourceUrl(
      `${AppSettings.URL_Settings}Pages/Staff_Image?StaffID=${user.StaffId}&${this.guid}`,
    );
  }

  getLanguages() {
    const langs = this.cacheService.getLanguage(
      this.cacheService.ActivePropertyID,
    );
    const withoutEnglish = langs.filter((lang) => lang.LanguageCode !== 'ENG');
    return withoutEnglish.length === 0 ? [] : langs.slice();
  }

  openIdeas() {
    window.open(this.IdeasURL, '_blank');
  }

  openDoc(tab) {
    if (tab == 1) {
      window.open(this.SupportURL, '_blank');
    } else if (tab == 2) {
      window.open(this.OnlineHelp, '_blank');
    } else if (tab == 3) {
      window.open(this.ReleaseNotes, '_blank');
    }
  }
  didSelectUserLanguage(lang: LanguageLookup) {}
}
